import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = '2141b0e5a5426a48205a077c9425f40f';

// ============ 통합 소스 판정 (canonical) ============
// 패밀리별 initial_*과 별개로, "이 방문의 소스가 뭔지"를 하나의 기준으로 판정한다.
// first_touch_* = 시간상 최초 유입 (register_once, 패밀리 구분 없음)
// session_*     = 최근 외부 유입 (register, 새 외부 유입마다 갱신)

const resolveCurrentSource = () => {
  const params = new URLSearchParams(window.location.search);
  const refHost = (() => {
    try {
      return document.referrer ? new URL(document.referrer).hostname : '';
    } catch {
      return '';
    }
  })();
  const isInternal = refHost === window.location.hostname;

  // 우선순위: 유료 파라미터 > 리퍼러 > 다이렉트
  if (params.get('utm_source')) {
    return {
      source: params.get('utm_source')!,
      medium: params.get('utm_medium') || 'utm',
      detail: params.get('utm_content') || params.get('utm_campaign') || '',
      isInternal: false,
    };
  }
  if (params.get('n_query') || params.get('n_keyword') || params.get('n_ad')) {
    return {
      source: 'naver_ad',
      medium: 'paid_search',
      detail: params.get('n_query') || params.get('n_keyword') || '',
      isInternal: false,
    };
  }
  if (params.get('gclid') || params.get('gbraid') || params.get('wbraid')) {
    return {
      source: 'google_ad',
      medium: 'paid',
      detail: params.get('g_keyword') || '',
      isInternal: false,
    };
  }
  if (refHost.includes('instagram')) {
    return { source: 'instagram_organic', medium: 'social', detail: refHost, isInternal: false };
  }
  if (refHost.includes('naver')) {
    return { source: 'naver_organic', medium: 'search', detail: refHost, isInternal: false };
  }
  if (refHost.includes('google')) {
    return { source: 'google_organic', medium: 'search', detail: refHost, isInternal: false };
  }
  if (refHost.includes('youtube')) {
    return { source: 'youtube', medium: 'social', detail: refHost, isInternal: false };
  }
  if (refHost.includes('facebook')) {
    return { source: 'facebook_organic', medium: 'social', detail: refHost, isInternal: false };
  }
  if (refHost.includes('daum')) {
    return { source: 'daum_organic', medium: 'search', detail: refHost, isInternal: false };
  }
  if (isInternal) {
    return { source: '', medium: '', detail: '', isInternal: true };
  }
  if (refHost) {
    return { source: refHost, medium: 'referral', detail: refHost, isInternal: false };
  }
  return { source: 'direct', medium: 'none', detail: '', isInternal: false };
};

const enrichCanonicalSource = () => {
  if (typeof window === 'undefined') return;

  const cur = resolveCurrentSource();

  // 내부 이동(사이트 내 페이지 전환)이면 기존 session_source 유지
  if (cur.isInternal) return;

  // last-touch: 새 외부 유입마다 덮어씀
  mixpanel.register({
    session_source: cur.source,
    session_medium: cur.medium,
    session_detail: cur.detail,
  });

  // first-touch: 시간상 최초 유입이 전부 가져감 (패밀리 분리 없음)
  mixpanel.register_once({
    first_touch_source: cur.source,
    first_touch_medium: cur.medium,
    first_touch_detail: cur.detail,
    first_touch_at: new Date().toISOString(),
    first_touch_landing: window.location.pathname,
  });
};

// ============ 광고 파라미터 자동 캡처 함수들 ============

const enrichNaverParams = () => {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);

  // 네이버 광고 추적 파라미터들
  const naverKeys = [
    'n_media',         // 매체 ID
    'n_query',         // 사용자가 실제로 친 검색어
    'n_rank',          // 광고 노출 순위
    'n_ad_group',      // 광고그룹 ID
    'n_ad',            // 광고 소재 ID
    'n_keyword_id',    // 키워드 ID
    'n_keyword',       // 등록한 키워드
    'n_campaign_type', // 캠페인 타입
    'n_ad_group_type', // 광고그룹 타입
    'n_match',         // 매칭 타입 (1=정확, 2=확장 등)
    'n_contract',      // 계약 정보
  ];

  const naverParams: Record<string, string> = {};
  naverKeys.forEach((key) => {
    const value = params.get(key);
    if (value) naverParams[key] = value;
  });

  if (Object.keys(naverParams).length > 0) {
    // 마지막 터치 (이후 모든 이벤트에 자동 첨부, 새 광고 클릭 시 갱신됨)
    mixpanel.register(naverParams);

    // 첫 터치 (최초 유입 시점만 기록, 이후 덮어쓰지 않음)
    const initialNaverParams: Record<string, string> = {};
    Object.entries(naverParams).forEach(([k, v]) => {
      initialNaverParams[`initial_${k}`] = v;
    });
    mixpanel.register_once(initialNaverParams);
  }
};

const enrichUtmParams = () => {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];

  const utmParams: Record<string, string> = {};
  utmKeys.forEach((key) => {
    const value = params.get(key);
    if (value) utmParams[key] = value;
  });

  if (Object.keys(utmParams).length > 0) {
    // 마지막 터치 (이후 모든 이벤트에 자동 첨부)
    mixpanel.register(utmParams);

    // 첫 터치 (최초 유입만 기록, 절대 덮어쓰지 않음)
    const initialUtmParams: Record<string, string> = {};
    Object.entries(utmParams).forEach(([k, v]) => {
      initialUtmParams[`initial_${k}`] = v;
    });
    mixpanel.register_once(initialUtmParams);
  }
};

const enrichGoogleParams = () => {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);

  // 구글 광고 추적 파라미터들
  const googleKeys = [
    'gclid',           // 구글 클릭 ID (자동 태깅)
    'gbraid',          // iOS 14+ 추적용
    'wbraid',          // 웹 추적용
    'g_network',       // g=검색, s=검색파트너, d=디스플레이, u=유튜브
    'g_device',        // m=모바일, c=PC, t=태블릿
    'g_matchtype',     // 매칭 유형
    'g_placement',     // 노출 위치
    'g_adposition',    // 광고 위치
    'g_creative',      // 광고 소재 ID (커스텀)
    'g_keyword',       // 키워드 (커스텀)
  ];

  const googleParams: Record<string, string> = {};
  googleKeys.forEach((key) => {
    const value = params.get(key);
    if (value) googleParams[key] = value;
  });

  if (Object.keys(googleParams).length > 0) {
    mixpanel.register(googleParams);

    const initialGoogleParams: Record<string, string> = {};
    Object.entries(googleParams).forEach(([k, v]) => {
      initialGoogleParams[`initial_${k}`] = v;
    });
    mixpanel.register_once(initialGoogleParams);
  }
};

const enrichReferrerInfo = () => {
  if (typeof window === 'undefined') return;

  // 첫 방문 시 referrer 정보도 잡아두기 (utm 없을 때 백업용)
  if (document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      mixpanel.register_once({
        initial_referrer_host: referrerUrl.hostname,
        initial_referrer_url: document.referrer,
      });
    } catch (e) {
      // referrer 파싱 실패 시 그냥 넘어감
    }
  }

  // 첫 랜딩 페이지도 기록
  mixpanel.register_once({
    initial_landing_path: window.location.pathname,
    initial_landing_url: window.location.href,
  });
};

// ============ Mixpanel 초기화 ============

mixpanel.init(MIXPANEL_TOKEN, {
  autocapture: true,
  record_sessions_percent: 100,
  persistence: 'localStorage', // 모바일 Safari/인스타 인앱 브라우저 호환성
  debug: process.env.NODE_ENV !== 'production',
  loaded: function () {
    // init 완료 후 실행 (타이밍 이슈 방지)
    enrichCanonicalSource(); // 통합 first_touch/session 판정 (가장 먼저)
    enrichUtmParams();
    enrichNaverParams();
    enrichGoogleParams();
    enrichReferrerInfo();

    if (process.env.NODE_ENV !== 'production') {
      console.log('[mixpanel] ✅ initialized');
      console.log('[mixpanel] first_touch_source:', mixpanel.get_property('first_touch_source'));
      console.log('[mixpanel] session_source:', mixpanel.get_property('session_source'));
      console.log('[mixpanel] initial_utm_source:', mixpanel.get_property('initial_utm_source'));
      console.log('[mixpanel] last utm_source:', mixpanel.get_property('utm_source'));
      console.log('[mixpanel] initial_referrer_host:', mixpanel.get_property('initial_referrer_host'));
    }
  },
});

// 전역 노출 (콘솔 디버깅용 - production에서도 안전)
if (typeof window !== 'undefined') {
  (window as any).mixpanel = mixpanel;
}

// ============ 외부에서 사용하는 함수들 ============

export const track = (event: string, properties?: Record<string, any>) => {
  mixpanel.track(event, properties);
};

export const getDistinctId = (): string => {
  return mixpanel.get_distinct_id();
};

// first-touch 프로필 고정용 공통 객체 (identifyUser / identifyFamily에서 공용)
const buildFirstTouchSetOnce = (attribution: Record<string, any>) => {
  return {
    first_seen_at: new Date().toISOString(),

    // 통합 first-touch (획득 분석은 이걸 기준으로)
    first_touch_source: attribution.first_touch_source,
    first_touch_medium: attribution.first_touch_medium,
    first_touch_detail: attribution.first_touch_detail,
    first_touch_at: attribution.first_touch_at,
    first_touch_landing: attribution.first_touch_landing,

    // 기존 패밀리별 first (세부 분석용, 그대로 유지)
    first_initial_utm_source: attribution.initial_utm_source,
    first_initial_utm_campaign: attribution.initial_utm_campaign,
    first_initial_utm_content: attribution.initial_utm_content,
    first_initial_n_keyword: attribution.initial_n_keyword,
    first_initial_n_query: attribution.initial_n_query,
    first_initial_referrer_host: mixpanel.get_property('initial_referrer_host'),
    first_initial_landing_path: mixpanel.get_property('initial_landing_path'),
  };
};

export const identifyUser = (phone: string, props?: Record<string, any>) => {

  if (!phone) {
    console.warn('[mixpanel] identifyUser: phone is required');
    return;
  }

  const currentId = mixpanel.get_distinct_id();

  // ⭐ 익명 ID와 phone alias 처리
  if (currentId && currentId !== phone && currentId.startsWith('$device:')) {
    try {
      mixpanel.alias(phone, currentId);
    } catch (e) {
      console.warn('[mixpanel] alias failed:', e);
    }
  }

  mixpanel.identify(phone);

  // 사용자 프로필에도 attribution 영구 저장 (재방문 시 비교용)
  const attribution = getAttributionProps();
  mixpanel.people.set({
    $phone: phone,
    ...props,
    ...attribution,
  });

  // 첫 attribution은 절대 덮어쓰지 않게 set_once
  mixpanel.people.set_once(buildFirstTouchSetOnce(attribution));
};

export const identifyFamily = (
  studentPhone: string,
  parentPhone: string,
  props?: Record<string, any>
) => {
  if (!studentPhone) {
    console.warn('[mixpanel] identifyFamily: studentPhone is required');
    return;
  }

  const currentId = mixpanel.get_distinct_id();

  // ⭐ 익명 ID와 학생폰 alias 처리 (이게 누락돼있었음!)
  if (currentId && currentId !== studentPhone && currentId.startsWith('$device:')) {
    try {
      mixpanel.alias(studentPhone, currentId);
    } catch (e) {
      console.warn('[mixpanel] alias anonymous failed:', e);
    }
  }

  mixpanel.identify(studentPhone);
  if (parentPhone && parentPhone !== studentPhone) {
    try {
      mixpanel.alias(parentPhone, studentPhone);
    } catch (e) {
      console.warn('[mixpanel] alias failed:', e);
    }
  }

  const attribution = getAttributionProps();

  mixpanel.people.set({
    $phone: studentPhone,
    student_phone: studentPhone,
    parent_phone: parentPhone,
    ...props,
    ...attribution,
  });

  // 첫 attribution은 절대 덮어쓰지 않게 set_once
  mixpanel.people.set_once(buildFirstTouchSetOnce(attribution));
};

export const getAttributionProps = () => {
  return {
    // ===== 통합 소스 판정 (canonical) =====
    // 획득 분석 = first_touch_source / 전환 분석 = session_source
    first_touch_source: mixpanel.get_property('first_touch_source'),
    first_touch_medium: mixpanel.get_property('first_touch_medium'),
    first_touch_detail: mixpanel.get_property('first_touch_detail'),
    first_touch_at: mixpanel.get_property('first_touch_at'),
    first_touch_landing: mixpanel.get_property('first_touch_landing'),
    session_source: mixpanel.get_property('session_source'),
    session_medium: mixpanel.get_property('session_medium'),
    session_detail: mixpanel.get_property('session_detail'),

    // ===== UTM - initial (최초 유입) =====
    initial_utm_source: mixpanel.get_property('initial_utm_source'),
    initial_utm_medium: mixpanel.get_property('initial_utm_medium'),
    initial_utm_campaign: mixpanel.get_property('initial_utm_campaign'),
    initial_utm_content: mixpanel.get_property('initial_utm_content'),
    initial_utm_term: mixpanel.get_property('initial_utm_term'),
    initial_utm_id: mixpanel.get_property('initial_utm_id'),

    // ===== UTM - last (최근 유입) =====
    last_utm_source: mixpanel.get_property('utm_source'),
    last_utm_medium: mixpanel.get_property('utm_medium'),
    last_utm_campaign: mixpanel.get_property('utm_campaign'),
    last_utm_content: mixpanel.get_property('utm_content'),
    last_utm_term: mixpanel.get_property('utm_term'),
    last_utm_id: mixpanel.get_property('utm_id'),

    // ===== 네이버 - initial =====
    initial_n_media: mixpanel.get_property('initial_n_media'),
    initial_n_query: mixpanel.get_property('initial_n_query'),
    initial_n_keyword: mixpanel.get_property('initial_n_keyword'),
    initial_n_keyword_id: mixpanel.get_property('initial_n_keyword_id'),
    initial_n_ad_group: mixpanel.get_property('initial_n_ad_group'),
    initial_n_ad_group_type: mixpanel.get_property('initial_n_ad_group_type'),
    initial_n_ad: mixpanel.get_property('initial_n_ad'),
    initial_n_rank: mixpanel.get_property('initial_n_rank'),
    initial_n_match: mixpanel.get_property('initial_n_match'),
    initial_n_campaign_type: mixpanel.get_property('initial_n_campaign_type'),
    initial_n_contract: mixpanel.get_property('initial_n_contract'),

    // ===== 네이버 - last =====
    last_n_media: mixpanel.get_property('n_media'),
    last_n_query: mixpanel.get_property('n_query'),
    last_n_keyword: mixpanel.get_property('n_keyword'),
    last_n_keyword_id: mixpanel.get_property('n_keyword_id'),
    last_n_ad_group: mixpanel.get_property('n_ad_group'),
    last_n_ad_group_type: mixpanel.get_property('n_ad_group_type'),
    last_n_ad: mixpanel.get_property('n_ad'),
    last_n_rank: mixpanel.get_property('n_rank'),
    last_n_match: mixpanel.get_property('n_match'),
    last_n_campaign_type: mixpanel.get_property('n_campaign_type'),
    last_n_contract: mixpanel.get_property('n_contract'),

    // ===== 구글 - initial =====
    initial_gclid: mixpanel.get_property('initial_gclid'),
    initial_gbraid: mixpanel.get_property('initial_gbraid'),
    initial_wbraid: mixpanel.get_property('initial_wbraid'),
    initial_g_network: mixpanel.get_property('initial_g_network'),
    initial_g_device: mixpanel.get_property('initial_g_device'),
    initial_g_matchtype: mixpanel.get_property('initial_g_matchtype'),
    initial_g_placement: mixpanel.get_property('initial_g_placement'),
    initial_g_creative: mixpanel.get_property('initial_g_creative'),
    initial_g_keyword: mixpanel.get_property('initial_g_keyword'),

    // ===== 구글 - last =====
    last_gclid: mixpanel.get_property('gclid'),
    last_gbraid: mixpanel.get_property('gbraid'),
    last_wbraid: mixpanel.get_property('wbraid'),
    last_g_network: mixpanel.get_property('g_network'),
    last_g_device: mixpanel.get_property('g_device'),
    last_g_matchtype: mixpanel.get_property('g_matchtype'),
    last_g_placement: mixpanel.get_property('g_placement'),
    last_g_creative: mixpanel.get_property('g_creative'),
    last_g_keyword: mixpanel.get_property('g_keyword'),

    // ===== Referrer / 랜딩 정보 =====
    initial_referrer_host: mixpanel.get_property('initial_referrer_host'),
    initial_referrer_url: mixpanel.get_property('initial_referrer_url'),
    initial_landing_path: mixpanel.get_property('initial_landing_path'),
    initial_landing_url: mixpanel.get_property('initial_landing_url'),
  };
};

export default mixpanel;